<template>
  <div class="coupons-section q-mt-xl q-mb-xl">
    <div class="row items-center q-mb-lg">
      <div class="col">
        <h2 class="text-h4 text-weight-bold featured-heading">Latest Coupons</h2>
        <div class="heading-underline"></div>
      </div>
      <div class="col-auto">
        <q-btn to="/coupons" flat color="primary" class="view-all-btn" icon-right="arrow_forward" label="View All" no-caps />
      </div>
    </div>
    <div v-if="loading" class="row q-col-gutter-md">
      <div class="col-12 col-sm-6 col-lg-4" v-for="i in 6" :key="i">
        <q-skeleton height="150px" square />
      </div>
    </div>
    <div v-else-if="error" class="text-center q-pa-xl bg-grey-2 rounded-borders">
      <q-icon name="error_outline" size="3rem" color="negative" />
      <p class="text-h6 text-negative q-mt-md">{{ error }}</p>
      <q-btn label="Try Again" color="primary" @click="$emit('refresh')" class="q-mt-md" no-caps />
    </div>
    <div v-else-if="coupons.length === 0" class="text-center q-pa-xl bg-grey-2 rounded-borders">
      <q-icon name="info" size="3rem" color="grey-7" />
      <p class="text-h6 text-grey-7 q-mt-md">No coupons available at the moment</p>
    </div>
    <div v-else class="row q-col-gutter-md">
        <div class="col-12 col-sm-6 col-lg-4" v-for="(coupon, index) in coupons" :key="coupon.id || index">
        <q-card class="coupon-card h-100" bordered @click="openCouponLink(coupon)">
          <q-card-section class="flex items-center no-padding">
            <div class="row full-width">
              <div class="col-auto q-pa-md">
                <q-avatar square size="54px" class="bg-white q-py-xs">
                  <q-img 
                    :src="coupon.store?.logo_url || '/images/placeholder.png'"
                    :alt="coupon.store?.name"
                    loading="lazy"
                    fit="contain"
                    @error="onImgError"
                  />
                </q-avatar>
              </div>
              <div class="col q-py-md q-pr-md">
                <div class="text-subtitle2 text-weight-medium ellipsis-2-lines">
                  {{ coupon.title }}
                </div>
                <div class="text-caption q-mt-xs q-mb-sm text-grey-7">
                  <q-icon name="store" size="12px" class="q-mr-xs" />
                  {{ coupon.store?.name }}
                </div>
              </div>
              <div v-if="coupon.discount_value" class="absolute-top-right q-ma-sm">
                <q-badge rounded color="green" class="discount-badge">
                  {{ coupon.discount_value }}{{ coupon.discount_type === 'percentage' ? '%' : '' }} OFF
                </q-badge>
              </div>
            </div>
          </q-card-section>
          <q-card-section v-if="coupon.code" class="coupon-code-section q-pa-sm bg-grey-2">
            <div class="row items-center justify-between">
              <div class="col text-center text-grey-7 text-caption">Code:</div>
              <div class="col text-center">
                <span class="code-display text-weight-medium">{{ coupon.code }}</span>
              </div>
              <div class="col text-center">
                <q-btn 
                  aria-label="Copy coupon code"
                  icon="content_copy"
                  flat
                  size="sm"
                  @click.stop="copyCouponCode(coupon.code)"
                />
              </div>
            </div>
          </q-card-section>
          <q-card-actions align="right" class="q-px-md q-pb-md">
            <q-space />
            <q-btn
              v-if="coupon.code"
              :label="'Get Code'"
              color="secondary"
              icon="content_copy"
              @click.stop="copyCouponCode(coupon.code)"
              no-caps
            />
            <q-btn
              v-else
              :label="'View Deal'"
              color="primary"
              icon="launch"
              @click.stop="openCouponLink(coupon)"
              no-caps
            />
          </q-card-actions>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useQuasar } from 'quasar';

const $q = useQuasar();

const props = defineProps({
  coupons: { type: Array, default: () => [] },
  loading: Boolean,
  error: String,
});
const emit = defineEmits(['refresh', 'open-coupon-link', 'img-error']);
const openCouponLink = (coupon) => emit('open-coupon-link', coupon);
const onImgError = (e) => emit('img-error', e);
const copyCouponCode = (code) => {
  navigator.clipboard.writeText(code)
    .then(() => {
      $q.notify({ message: 'Code copied!', color: 'positive', position: 'top', timeout: 1500, icon: 'content_copy' });
    })
    .catch(() => {
      $q.notify({ message: 'Failed to copy code', color: 'negative', position: 'top', timeout: 1500 });
    });
};
</script>
